// nebula/world.js: the nebula world (chapter 0 'ignite' and chapter 4 'brand').
// Volumetric raymarch of baked 3D density (macro structure + tiling detail noise +
// baked light transmittance), an analytic protostar (cocoon, disk, jets),
// crisp analytic stars and sparse foreground dust glints.
//
// params: mode 'ignite'|'brand', brand (string), seed (optional number, overrides brand),
//         shiftX, shiftY, igniteT (s since ignition, -1 before), brandT (s since the brand was set)
// input.hold (ignite): 0..1 press-and-hold collapse. The shell keeps hold = 1 once lit.
import * as THREE from 'three';
import { FullScreenQuad, clamp, lerp, smooth, seg, damp, easeInOut, easeOut, rng } from '../lib/common.js';
import * as G from './glsl.js';
import { MARCH, VIS } from './march.js';
import { COMPOSITE, MOTES_V, MOTES_F } from './comp.js';
import { BOX_MIN, BOX_MAX, igniteLook, brandLook, hashBrand } from './looks.js';

// per-tier budget. res = internal raymarch resolution in CSS px.
const TIERS = {
  high: { res: 0.44, resMax: 0.8, px: 250000, steps: 30, det2: 1, tap: 1, tapd: 1, macro: [176, 112, 120], light: [88, 56, 60], detail: 128, motes: 260, snapSteps: 120 },
  mid: { res: 0.4, resMax: 0.62, px: 100000, steps: 26, det2: 1, tap: 1, tapd: 0, macro: [136, 88, 96], light: [68, 44, 48], detail: 96, motes: 160, snapSteps: 90 },
  low: { res: 0.34, resMax: 0.5, px: 55000, steps: 20, det2: 0, tap: 1, tapd: 0, macro: [104, 68, 72], light: [52, 34, 36], detail: 64, motes: 90, snapSteps: 64 },
};

const v3 = (a) => new THREE.Vector3(a[0], a[1], a[2]);
const V2 = () => ({ value: new THREE.Vector2() });
const V3 = () => ({ value: new THREE.Vector3() });
const V4 = () => ({ value: new THREE.Vector4() });
const F = (x = 0) => ({ value: x });
const sq = (x) => x * x;
// dev-only multipliers (params k_*), default 1
const kParam = (P, k) => (P[k] === undefined || P[k] === '' ? 1 : +P[k]);

function mk3D(w, h, d, repeat, type = THREE.UnsignedByteType) {
  const rt = new THREE.WebGL3DRenderTarget(w, h, d, { depthBuffer: false });
  const t = rt.texture;
  t.format = THREE.RGBAFormat; t.type = type;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = t.wrapR = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  return rt;
}

// The tiling detail-noise volume is identical for every instance: bake it once per
// renderer and size and share it between the ignite and brand slots (ref-counted).
const DETAIL_CACHE = new Map();
function acquireDetail(renderer, N, bake) {
  let per = DETAIL_CACHE.get(renderer);
  if (!per) { per = new Map(); DETAIL_CACHE.set(renderer, per); }
  let e = per.get(N);
  if (!e) { e = { rt: mk3D(N, N, N, true), refs: 0, baked: false }; per.set(N, e); }
  e.refs++;
  if (!e.baked) { bake(e.rt); e.baked = true; }
  return e;
}
function releaseDetail(renderer, N) {
  const per = DETAIL_CACHE.get(renderer); if (!per) return;
  const e = per.get(N); if (!e) return;
  if (--e.refs <= 0) { e.rt.dispose(); per.delete(N); }
}

export function create(renderer, opts = {}) {
  const tierName = TIERS[opts.tier] ? opts.tier : 'mid';
  const TIER = TIERS[tierName];
  const half = opts.half !== false;
  const gl = renderer.getContext();
  const boxMin = v3(BOX_MIN), boxMax = v3(BOX_MAX);
  const boxSize = boxMax.clone().sub(boxMin);

  /* ---------------- shared uniforms ---------------- */
  const U = {
    uVol: F(null), uLit: F(null), uDet: F(null),
    uBoxMin: { value: boxMin }, uBoxMax: { value: boxMax },
    uBoxInv: { value: new THREE.Vector3(1 / boxSize.x, 1 / boxSize.y, 1 / boxSize.z) },
    uTime: F(0),
    uStar: V3(), uAxis: V3(), uB1: V3(), uB2: V3(),
    uMed: V4(), uGasP: V4(), uRedden: V3(), uGasF: V4(), uAlbC: V4(), uDep: V4(), uCavD: V4(), uNearK: F(0), uVisCav: F(0), uR8: V4(), uR8b: V4(), uR8c: V4(), uR9: V4(), uVeilY: V4(), uAlb8: V3(), uVeilC: V3(), uPull: V4(),
    uCoc: V4(), uHeat: V4(), uDisk: V4(), uShellB: V4(), uStir: V4(), uStirO: V3(),
    uLDir: V3(), uLCol: V3(), uAlb: V3(), uAlbL: V3(), uHeatA: V3(), uHeatB: V3(),
    uCamPos: V3(), uCamMat: { value: new THREE.Matrix3() }, uTan: V2(), uShift: V2(),
    uPxA: F(0.001), uDetN: F(TIER.detail), uStepK: F(1.45 / TIER.steps), uFloorF: V2(), uSAx: V3(), uSB1: V3(), uSB2: V3(),
    uLightP: V4(), uBand: F(0), uStarCol: V3(), uSW: F(0), uDustE: V3(), uFill: V4(), uFillC: V3(),
    uCO: V3(), uCH: V3(), uCS: V3(), uCR: V3(), uCR2: V3(), uCG: V3(), uAmb: V3(), uRim: V4(),
    uGain: V4(), uJet: V4(), uStr: V4(), uRHz: V4(), uShellCol: V3(), uBgA: V3(), uBgB: V3(), uBgI: F(0), uLI: F(0), uHz: V4(),
  };
  U.uHeatA.value.set(1.0, 0.08, 0.32);
  U.uHeatB.value.set(0.8, 0.88, 1.0);
  U.uPull.value.set(0, 0, 2, 0);

  const marchDefs = (steps, det2, tap, tapd) => ({ STEPS: steps, DET2: det2, TAP: tap, TAPD: tapd, LDR: half ? 0 : 1, R8: 0, R9: 0 });
  const mkMat = (frag, uniforms, defines = {}) => new THREE.ShaderMaterial({
    vertexShader: G.VERT, fragmentShader: frag, uniforms, defines, depthTest: false, depthWrite: false,
  });
  const marchMat = mkMat(MARCH, U, marchDefs(TIER.steps, TIER.det2, TIER.tap, TIER.tapd));
  // r8: the Cosmic Cliffs terms are compiled only into the lit-ignite programs (R8=1); every other beat runs the r7 program (r7 GPU cost)
  const marchMat8 = mkMat(MARCH, U, Object.assign(marchDefs(TIER.steps, TIER.det2, TIER.tap, TIER.tapd), { R8: 1 }));
  // r9: pre-integrated skins, crisp rims, dome breakup: R9=1 programs, lit ignite only (k_r9 = 0 runs the r8 programs exactly)
  const marchMat9 = mkMat(MARCH, U, Object.assign(marchDefs(TIER.steps, TIER.det2, TIER.tap, TIER.tapd), { R8: 1, R9: 1 }));
  const visMat = mkMat(VIS, U); visMat.vertexShader = G.VERT0;
  const CU = {
    uVolT: F(null), uVis: F(null), uVolTexel: V2(),
    uCamMat: U.uCamMat, uTan: U.uTan, uShift: U.uShift,
    uCoreCol: V3(), uCoreI: F(0), uCoreR: F(2), uSpike: F(0), uSpikeL: F(40), uHaloI: F(0), uHaloR: F(20), uVisMix: F(0),
    uStarsI: F(1), uFgStars: F(0.15), uPxA: F(0.001), uTime: U.uTime, uDpr: F(1),
    uFlashCol: V3(), uFlash: F(0), uFlashR: F(100), uStarPx: V2(), uStarOn: F(1), uSoft: F(0),
    uDet: U.uDet, uDetN: U.uDetN, uCamPos: U.uCamPos, uStarW: U.uStar, uLDirW: U.uLDir,
    uFwd: V3(), uLDirS: V2(), uRefD: F(7), uRefR: F(2), uRimS: F(0), uEdge: V4(), uEdge2: V4(), uEdge3: V4(), uEdge4: V4(), uEdgeR: V2(), uRimM: V4(), uVisN: F(0), uHaloC: V4(), uR9C: V4(), uR9D: V4(), uR9U: V3(),
  };
  const compMat = mkMat(COMPOSITE, CU, { LDR: half ? 0 : 1, SHARP: 1, EDGE: tierName === 'low' ? 0 : 1, R8: 0, R9: 0 });
  const compMat8 = mkMat(COMPOSITE, CU, { LDR: half ? 0 : 1, SHARP: 1, EDGE: tierName === 'low' ? 0 : 1, R8: 1, R9: 0 });
  const compMat9 = mkMat(COMPOSITE, CU, { LDR: half ? 0 : 1, SHARP: 1, EDGE: tierName === 'low' ? 0 : 1, R8: 1, R9: 1 });
  const DU = { uSrc: F(null), uTexel: V2() };
  const denMat = mkMat(G.DENOISE, DU);
  const denQ = new FullScreenQuad(denMat);
  const marchQ = new FullScreenQuad(marchMat), visQ = new FullScreenQuad(visMat), compQ = new FullScreenQuad(compMat);
  const bakeQ = new FullScreenQuad(null);

  /* ---------------- targets ---------------- */
  const rtType = half ? THREE.HalfFloatType : THREE.UnsignedByteType;
  const mkRT = (w, h, type = rtType) => new THREE.WebGLRenderTarget(Math.max(1, w | 0), Math.max(1, h | 0), {
    type, depthBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
  });
  const volRT = mkRT(4, 4), volRT2 = mkRT(4, 4);
  // params.backdrop = 1: cheap mode for the (blurred) offer backdrop: 0.4x internal res, 0.6x steps, soft upsample, no stars/motes
  const volRTb = mkRT(4, 4), volRT2b = mkRT(4, 4);
  let volWb = 4, volHb = 4;
  const visRT = mkRT(1, 1, THREE.UnsignedByteType);
  const N = TIER.detail;
  const macroRT = mk3D(TIER.macro[0], TIER.macro[1], TIER.macro[2], false, (opts.macroHalf !== false && half) ? THREE.HalfFloatType : THREE.UnsignedByteType);
  // light transmittance in half float: 8-bit sqrt(T) bands into iso-contours near the bright star
  const litRT = mk3D(TIER.light[0], TIER.light[1], TIER.light[2], false, half ? THREE.HalfFloatType : THREE.UnsignedByteType);
  let detail = null;
  U.uVol.value = macroRT.texture; U.uLit.value = litRT.texture;
  CU.uVolT.value = volRT2.texture; CU.uVis.value = visRT.texture;
  DU.uSrc.value = volRT.texture;

  function bake3D(rt, mat, depth) {
    const ac = renderer.autoClear;
    const prev = renderer.getRenderTarget();
    renderer.autoClear = false;
    bakeQ.material = mat;
    for (let z = 0; z < depth; z++) {
      mat.uniforms.uZ.value = (z + 0.5) / depth;
      renderer.setRenderTarget(rt, z);
      bakeQ.render(renderer);
    }
    renderer.setRenderTarget(prev);
    renderer.autoClear = ac;
  }
  function mip3D(rt) {
    const tex = renderer.properties.get(rt.texture).__webglTexture;
    if (!tex) return;
    renderer.state.bindTexture(gl.TEXTURE_3D, tex);
    gl.generateMipmap(gl.TEXTURE_3D);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    renderer.state.unbindTexture();
  }

  /* ---------------- bake materials ---------------- */
  const detMat = new THREE.ShaderMaterial({ vertexShader: G.VERT0, fragmentShader: G.BAKE_DETAIL, uniforms: { uZ: F(0), uN: F(N) }, depthTest: false, depthWrite: false });
  const vec4s = (n) => Array.from({ length: n }, () => new THREE.Vector4());
  const MU = {
    uZ: F(0), uRes: { value: new THREE.Vector3(...TIER.macro) }, uBoxMin: { value: boxMin }, uBoxSize: { value: boxSize }, uSeed: F(0),
    uWarp: V4(), uWall: V4(), uWallC: V3(), uWallOpen: V3(), uWall2: V4(),
    uPB: { value: vec4s(6) }, uPT: { value: vec4s(6) }, uGlob: { value: vec4s(8) },
    uShell: V4(), uShellC: V3(), uLobe: V4(), uLobeAx: V3(), uRing: V4(), uRingN: V3(),
    uCliff: V4(), uCliff2: V4(), uLanes: V4(), uGas: V4(), uStar: V3(), uFront: V4(), uLDirM: V3(),
  };
  const macroMat = new THREE.ShaderMaterial({ vertexShader: G.VERT0, fragmentShader: G.BAKE_MACRO, uniforms: MU, depthTest: false, depthWrite: false });
  const LU = {
    uSrc: { value: macroRT.texture }, uZ: F(0), uRes: { value: new THREE.Vector3(...TIER.light) },
    uBoxMin: { value: boxMin }, uBoxMax: { value: boxMax }, uBoxSize: { value: boxSize },
    uStar: V3(), uLDir: V3(), uLFill: V3(), uExt: F(3), uExtL: F(3), uStep: F(0.09), uClr: V4(),
  };
  const litMat = new THREE.ShaderMaterial({ vertexShader: G.VERT0, fragmentShader: G.BAKE_LIGHT, uniforms: LU, depthTest: false, depthWrite: false });

  /* ---------------- looks ---------------- */
  let look = null, lookKey = '';
  function setLook(L) {
    look = L;
    MU.uSeed.value = L.seed;
    MU.uWarp.value.fromArray(L.warp);
    const w = L.wall;
    MU.uWall.value.set(w ? 1 : 0, w ? w.r : 0, w ? w.noise : 0, w ? w.cover : 0);
    if (w) { MU.uWallC.value.fromArray(w.c); MU.uWallOpen.value.fromArray(w.open); MU.uWall2.value.set(w.holes, w.thick, 0, 0); }
    for (let i = 0; i < 6; i++) {
      const pl = L.pillars[i];
      if (pl) { MU.uPB.value[i].set(pl[0], pl[1], pl[2], pl[3]); MU.uPT.value[i].set(pl[4], pl[5], pl[6], pl[7]); }
      else { MU.uPB.value[i].set(0, 0, 0, 0); MU.uPT.value[i].set(0, 1, 0, 0); }
    }
    for (let i = 0; i < 8; i++) { const g = L.globs[i]; if (g) MU.uGlob.value[i].set(g[0], g[1], g[2], g[3]); else MU.uGlob.value[i].set(0, 0, 0, 0); }
    const s = L.shell; MU.uShell.value.set(s ? 1 : 0, s ? s.r : 0, s ? s.thick : 1, s ? s.frag : 0); if (s) MU.uShellC.value.fromArray(s.c);
    const lb = L.lobe; MU.uLobe.value.set(lb ? 1 : 0, lb ? lb.len : 1, lb ? lb.width : 1, lb ? lb.waist : 0); if (lb) MU.uLobeAx.value.fromArray(lb.ax);
    const rg = L.ring; MU.uRing.value.set(rg ? 1 : 0, rg ? rg.r : 1, rg ? rg.tube : 1, rg ? rg.clump : 0); if (rg) MU.uRingN.value.fromArray(rg.n);
    const c = L.cliff; MU.uCliff.value.set(c ? 1 : 0, c ? c.top : 0, c ? c.amp : 0, c ? c.freq : 0);
    MU.uCliff2.value.set(c && c.face !== undefined ? c.face : 99, c && c.faceAmp || 0, c && c.faceFreq || 0.4, c && c.foot && opts.mobile ? c.foot : 0);
    MU.uLanes.value.fromArray(L.lanes);
    MU.uGas.value.fromArray(L.gas);
    MU.uFront.value.fromArray(L.front);
    MU.uStar.value.fromArray(L.star); MU.uLDirM.value.fromArray(L.lDir);
    LU.uClr.value.set(L.cavR * 0.5, L.cavR * 1.3, 0, 0);
    LU.uStar.value.fromArray(L.star); LU.uLDir.value.fromArray(L.lDir); LU.uLFill.value.fromArray(L.fill.dir);
    U.uFill.value.set(L.fill.i, L.fill.y0, L.fill.y1, 0); U.uFillC.value.fromArray(L.fill.c);
    LU.uExt.value = L.med[3] * L.med[0] * 0.5; LU.uExtL.value = L.med[3] * L.med[0] * L.extL;
    LU.uStep.value = Math.min(boxSize.x / TIER.macro[0], boxSize.y / TIER.macro[1]) * 1.1;
    U.uStar.value.fromArray(L.star);
    U.uAxis.value.fromArray(L.axis).normalize();
    const b1 = U.uB1.value.set(0.31, 0.17, 0.93).cross(U.uAxis.value).normalize();
    U.uB2.value.copy(U.uAxis.value).cross(b1).normalize();
    { const nf = ['ring', 'bipolar', 'bubble'].includes(L.kind); U.uFloorF.value.set(nf ? 0 : 1, nf ? 0.14 : 0); }
    U.uMed.value.fromArray(L.med); U.uGasP.value.fromArray(L.gasP); U.uGasP.value.w = L.veilStar; U.uRedden.value.fromArray(L.redden);
    U.uLDir.value.fromArray(L.lDir); U.uLCol.value.fromArray(L.lCol); U.uAlb.value.fromArray(L.alb); U.uAlbL.value.fromArray(L.albL);
    U.uCO.value.fromArray(L.cO); U.uCH.value.fromArray(L.cH); U.uCS.value.fromArray(L.cS); U.uCR.value.fromArray(L.cR); U.uCR2.value.fromArray(L.cR2); U.uCG.value.fromArray(L.cG);
    U.uAmb.value.fromArray(L.amb); U.uDustE.value.fromArray(L.dustE); U.uGain.value.fromArray(L.gain); U.uShellCol.value.fromArray(L.shellCol);
    U.uHz.value.fromArray(L.hz);
    U.uBgA.value.fromArray(L.bgA); U.uBgB.value.fromArray(L.bgB); U.uBgI.value = L.bgI;
    U.uStarCol.value.fromArray(L.starCol);
    bake3D(macroRT, macroMat, TIER.macro[2]);
    bake3D(litRT, litMat, TIER.light[2]);
  }
  function ensureLook(P) {
    const mode = P.mode === 'brand' ? 'brand' : 'ignite';
    let seed = 0;
    if (mode === 'brand') seed = P.seed !== undefined && P.seed !== '' && Number.isFinite(+P.seed) ? (+P.seed >>> 0) : hashBrand(P.brand || 'Starlex');
    const key = mode + ':' + seed;
    if (key === lookKey) return;
    lookKey = key;
    setLook(mode === 'brand' ? brandLook(seed) : igniteLook());
  }

  /* ---------------- motes: a few tiny glints of foreground dust ---------------- */
  const NM = TIER.motes;
  const mpos = new Float32Array(NM * 3), msz = new Float32Array(NM);
  const r = rng(91);
  for (let i = 0; i < NM; i++) { mpos[i * 3] = r(); mpos[i * 3 + 1] = r(); mpos[i * 3 + 2] = r(); msz[i] = Math.pow(r(), 3.0); }
  const mgeo = new THREE.BufferGeometry();
  mgeo.setAttribute('position', new THREE.BufferAttribute(mpos, 3));
  mgeo.setAttribute('aSize', new THREE.BufferAttribute(msz, 1));
  const MOU = {
    uTime: U.uTime, uL: F(7), uPx: F(1), uStarL: F(0), uCool: F(0.02),
    uStarW: U.uStar, uCamW: U.uCamPos, uWarmC: V3(), uCoolC: V3(), uShift: U.uShift,
  };
  MOU.uCoolC.value.set(0.4, 0.9, 1.0);
  MOU.uWarmC.value.set(1.0, 0.4, 0.75);
  const moteMat = new THREE.ShaderMaterial({
    vertexShader: MOTES_V, fragmentShader: MOTES_F, uniforms: MOU,
    transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const motes = new THREE.Points(mgeo, moteMat);
  motes.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(motes);
  const camera = new THREE.PerspectiveCamera(48, 1, 0.05, 200);

  /* ---------------- frame state ---------------- */
  const S = {
    mode: 'ignite', hold: 0, p: 0, tau: -1, time: 0, lookX: 0, lookY: 0,
    camPos: new THREE.Vector3(), camTgt: new THREE.Vector3(), roll: 0, tanV: 0.445, shiftX: 0, shiftY: 0,
    stir: 0, stirX: 0.5, stirY: 0.5,
  };
  const grade = {
    exposure: 1.0, bloomK: 0.7, threshold: 0.9, sat: 1.06, contrast: 1.05, vignette: 0.45, grain: 0.03, ca: 0.0,
    shadows: [0.02, 0.02, 0.09], highs: [0.05, 0.025, -0.01], gainC: [1, 1, 1],
  };
  let bufW = 4, bufH = 4, volW = 4, volH = 4;
  const anchors = { star: { x: 0.5, y: 0.5, visible: true } };
  const _v = new THREE.Vector3(), _t = new THREE.Vector3(), _q = new THREE.Vector3();
  function orbit(out, d, az, el) { // camera position around the star: az about +y (0 = +z), el up
    const ce = Math.cos(el);
    return out.set(Math.sin(az) * ce * d, Math.sin(el) * d, Math.cos(az) * ce * d).add(U.uStar.value);
  }

  // Everything time/progress/params dependent, in plain numbers (no allocation).
  // Ignite beats: hold 0..1 = collapse (camera pushes in, gas is pulled in and wound up,
  // cocoon -> disk -> jets). Ignition clock tau (s) = params.igniteT from the shell, or
  // (harness / deep links, hold = 1) progress 0..0.15 -> 0..2.4 s: burst, light front,
  // recoil. Then progress 0.12..0.82 drifts through the lit nebula and 0.82..1 pushes
  // into the star for the flash cut. Brand: 0..0.18 collapse, ignition from 0.18,
  // re-ignition on every new brand (params.brandT).
  function computeState(input, time) {
    const P = input.params || {};
    const brand = P.mode === 'brand';
    const p = clamp(+input.progress || 0, 0, 1);
    let h, tau = -1, rebirth = false;
    if (!brand) {
      h = clamp(+input.hold || 0, 0, 1);
      if (h >= 0.999) tau = p / 0.15 * 2.4;
      const tS = P.igniteT === undefined ? -1 : +P.igniteT;
      if (Number.isFinite(tS) && tS >= 0) tau = Math.max(tau, tS);
    } else {
      h = 0.85 * smooth(0.0, 0.18, p);
      if (p >= 0.18) tau = (p - 0.18) / 0.3 * 2.4;
      const tB = P.brandT === undefined ? -1 : +P.brandT;
      if (tau >= 0 && Number.isFinite(tB) && tB >= 0 && tB < 3.2 && tB < tau) { tau = tB; rebirth = true; }
    }
    const lit = tau >= 0 ? 1 : 0;
    const K = (k) => kParam(P, k);
    S.mode = brand ? 'brand' : 'ignite'; S.p = p; S.hold = h; S.tau = tau; S.time = time;
    const hE = easeInOut(h);
    const beat = Math.pow(0.5 + 0.5 * Math.sin(time * 3.0 + 16.0 * h * h), 8);
    const pulse = 1 + h * h * (0.3 * beat + 0.04 * h * Math.sin(time * 31.0));
    const tz = Math.max(tau, 0);
    const blow = lit * smooth(0.0, 0.9, tz);
    const pre = 1 - blow;
    // r8 Cosmic Cliffs weight (ignite, eases in with the blow-out; k_r8 = 0 restores the r7 look exactly)
    const r8L = brand ? null : look.r8, r8k = r8L ? K('k_r8') : 0, r8 = blow * r8k;
    S.r8 = r8 > 1e-4 ? 1 : 0;
    const r9L = r8L && look.r9 && !(+P.backdrop > 0) ? look.r9 : null, r9 = r9L ? r8 * K('k_r9') : 0, ph9 = opts.mobile || (input.aspect || 1.6) < 1;
    S.r9 = r9 > 1e-4 ? 1 : 0;   // r9: picks the R9 programs (k_r9 = 0: the r8 programs + r8 uniforms)   // picks the R8 programs (k_r8 = 0: the r7 programs + r7 uniforms)
    const push8 = r8L ? smooth(0.82, 1.0, p) * lit : 0;   // r8: the push-in into the star's cavity (walls + teal core come back up)
    // --- gravity: pull + wind the cloud toward the core (stateless: function of hold)
    U.uPull.value.set(0.95 * hE * pre * K('k_pull'), 2.6 * hE * pre * K('k_pull'), lerp(1.6, 1.25, h), 0);
    // --- protostellar cocoon, disk, jets
    const cocD = smooth(0.02, 0.4, h) * lerp(0.6, 1.3, h) * K('k_coc');
    U.uCoc.value.set(lerp(1.7, 1.0, hE) * (1 + 1.6 * blow), cocD * (1 - blow), lerp(0.5, 1.7, h), h);
    const heatI = (2.4 + 30.0 * h * h + 40.0 * Math.pow(h, 8)) * Math.max(0.12, smooth(0.0, 0.2, h)) * pulse * pre * 0.18 * K('k_heat');
    U.uHeat.value.set(lerp(0.55, 0.32, h), heatI, lerp(0.15, 1.0, h), (0.06 + 0.9 * h * h) * Math.max(0.2, smooth(0.0, 0.2, h)) * pulse * pre);
    U.uDisk.value.set(smooth(0.28, 0.72, h) * 0.8 * (1 - smooth(0.0, 0.5, tz) * lit), lerp(0.3, 0.62, h), (0.8 + 4.5 * h * h) * pulse, 0.11);
    U.uJet.value.set(smooth(0.2, 0.55, h) * (1 - smooth(0.1, 0.9, tz) * lit), lerp(0.5, 3.2, smooth(0.2, 1.0, h)) + 2.0 * blow, (0.8 + 1.8 * smooth(0.2, 0.7, h) * pre) * pulse, 0.075);
    // --- the star's light: a front that races outward at ignition
    const fr = lit * easeOut(clamp(tz / 2.3, 0, 1));
    const starL = lit * look.starL * smooth(0.0, 0.12, tz) * (0.45 + 0.5 * Math.exp(-tz / 0.5)) * K('k_star');
    U.uLightP.value.set(starL, 2.2, 0.25 + fr * 17.0, 0.5 + 2.4 * fr);
    U.uBand.value = lit * 1.2 * smooth(0.0, 0.12, tz) * (1 - smooth(0.5, 2.3, tz)) * (brand ? 0.6 : 1);
    const fs = easeOut(clamp(tz / 1.3, 0, 1));
    U.uShellB.value.set(0.3 + fs * 6.0, 0.1 + fs * 0.25, lit * 0.35 * smooth(0.0, 0.08, tz) * (1 - smooth(0.3, 1.3, tz)), 1.2);
    // r7: uGasF was dead up to r6 (x/y/z/w were set on the uniform wrapper, not on .value): every look was tuned
    // with front gas, cluster gas and the cavity at 0, tap self-shadow 1 (Vector4 default w). Now the cavity
    // ramps in after ignition, ignite only; the self-shadow eases to tapK once lit (approved collapse + brand frames unchanged; k_gbr=1 tries
    // brands); front/cluster gas stay off (k_gfx / k_gfy = 1 to try them).
    const gOn = brand ? (+P.k_gbr || 0) : 1;
    U.uGasF.value.set(look.frontGas * K('k_fgas') * (+P.k_gfx || 0), look.clusterGas * K('k_veil') * (+P.k_gfy || 0),
      gOn * K('k_cav') * lit * look.cavR * easeOut(clamp(tz / 1.6, 0, 1)), (brand ? 1 : lerp(1, look.tapK + (r8L ? (r8L.tapK - look.tapK) * r8k * K('k_tk') : 0), blow)) * K('k_tap'));   // w: r6 ran the self-shadow at 1 (Vector4 default)
    U.uVisCav.value = U.uGasF.value.z * (+P.k_vc || 0);
    U.uNearK.value = brand ? 0 : 0.9 * blow * K('k_nk');
    { const cd = _v.copy(U.uCamPos.value).sub(U.uStar.value).normalize(); U.uCavD.value.set(cd.x, cd.y, cd.z, brand ? 0 : (look.cavOpen || 0) * K('k_co')); }
    { const dp = brand ? [0, 0, 0] : (look.dep || [0, 0, 0]);
      U.uDep.value.set(dp[0] * blow * K('k_dx'), dp[1] * blow * K('k_dy'), (r8L ? lerp(dp[2], r8L.depZ, r8k * K('k_dz8')) : dp[2]) * blow * K('k_dz'), brand ? 0 : K('k_sm')); }
    { const ac = look.albC || [1, 1, 1, 0], a8 = r8L ? r8L.albC : ac, k8 = r8 * K('k_an');   // r8: the star-lit wall dust scatters rust-peach, not magenta
      U.uAlbC.value.set(lerp(ac[0], a8[0], k8), lerp(ac[1], a8[1], k8), lerp(ac[2], a8[2], k8), brand ? 0 : ac[3] * K('k_ac') * blow); }
    // external cluster light: the key light before ignition, a fill after
    const ext = brand ? smooth(0.0, 0.35, p) : 1;
    U.uLI.value = look.lI * ext * lerp(1.0, look.lIlit, blow) * K('k_lI');
    U.uBgI.value = look.bgI * ext;
    U.uSW.value = smooth(0.0, 1.2, tz) * lit * look.swLit;
    U.uRim.value.set(look.rim[0] * lerp(1.0, look.rimLit, blow) * K('k_rim'), look.rim[1] * K('k_rim2'), 0, 0);
    U.uGain.value.set(look.gain[0] * (look.gxK || 1) * K('k_gx'), look.gain[1] * K('k_gy'), look.gain[2] * K('k_gz'), look.gain[3]);
    U.uBgI.value *= K('k_bg');
    U.uFill.value.x = look.fill.i * K('k_fill');
    // r8: body shading (rust albedo, carving, thin skins/fronts, no bulk magenta glow), veils, deeper rust fill
    U.uR8.value.set(r8 * K('k_sk') * (1 - (r8L ? r8L.push.sk : 0) * push8 * K('k_ps')), r8 * K('k_cv'), r8 * K('k_mk'), r8L ? r8 * r8L.veil * K('k_vl') * (1 - push8 * r8L.push.vl) : 0);
    U.uR8b.value.set(r8 * K('k_ab'), r8 * K('k_gf'), r8L ? r8 * r8L.gd * K('k_gd') : 0, r8 * K('k_bp'));
    // r8 folds (exact, on the CPU): rust albedo into uAlb/uAlbL, no bulk magenta glow (uGain.y), thinner cluster skins (uRim.x)
    U.uAlb.value.fromArray(look.alb); U.uAlbL.value.fromArray(look.albL);
    if (r8 > 0) { const ka = r8 * K('k_ab'); U.uAlb.value.lerp(_q.fromArray(r8L.alb), ka); U.uAlbL.value.lerp(_q.fromArray(r8L.alb), ka);
      U.uGain.value.y *= 1 - r8 * K('k_mk'); U.uRim.value.x *= 1 - 0.45 * U.uR8.value.x; }
    if (r8L) U.uFill.value.x *= lerp(1, (opts.mobile || (input.aspect || 1.6) < 1) ? r8L.fillIp : r8L.fillI, r8 * K('k_fi'));
    U.uR8c.value.set(r8L ? lerp(0.85, r8L.nsc, r8 * K('k_ns')) : 0.85, r8L ? lerp(1, r8L.ct, r8 * K('k_ct')) : 1, 0, 0);   // y: cavity teal-core scale (teal over rust read grey)
    if (r8L) { U.uAlb8.value.fromArray(r8L.alb); U.uVeilC.value.fromArray(r8L.veilC); U.uVeilY.value.fromArray(r8L.veilY); }
    U.uDustE.value.fromArray(look.dustE); if (r8L) U.uDustE.value.multiplyScalar(lerp(1, r8L.dE, r8 * K('k_de')));   // r8: shadowed dust glows rust, not black
    U.uFillC.value.fromArray(look.fill.c); if (r8 > 0) U.uFillC.value.lerp(_q.fromArray(r8L.fillC), r8 * K('k_fc'));
    U.uCO.value.fromArray(look.cO); if (r8 > 0) U.uCO.value.lerp(_q.fromArray(r8L.cO), r8 * K('k_oc'));
    // r9: skin pre-integration + gain, dome breakup; lit rust ridges (fill, dust glow, star scatter), phones most
    U.uR9.value.set(K('k_pi'), r9L ? lerp(1, r9L.skG, r9 * K('k_sg')) : 1, r9 * K('k_dm') * (1 - push8), r9 * K('k_dw') * (1 - push8));
    if (r9L) { const kp = r9 * K('k_ph');
      U.uFill.value.x *= lerp(1, ph9 ? r9L.fillP : r9L.fillD, kp); U.uGain.value.z *= lerp(1, ph9 ? r9L.gzP : r9L.gzD, kp);
      if (ph9) U.uDustE.value.multiplyScalar(lerp(1, r9L.dEP, kp)); }   // r8: bluer OIII (turquoise gas over rust dust read khaki)
    // --- composite: protostar core / new star, halo, spikes, burst
    const preCore = (0.4 * smooth(0.1, 0.4, h) + 2.4 * h * h + 30.0 * Math.pow(h, 10)) * pulse;
    const burst = lit * (rebirth ? Math.exp(-tz / 0.3) : smooth(0.0, 0.05, tz) * Math.exp(-Math.max(tz - 0.05, 0) / 0.3));
    CU.uCoreI.value = lerp(preCore * 0.5 * K('k_core'), 40.0, blow) + 60.0 * burst;
    const hc = CU.uCoreCol.value;
    hc.set(1.0, lerp(0.14, 0.72, h * h), lerp(0.04, 0.42, h * h * h)).lerp(_v.fromArray(look.coreCol), blow);
    CU.uCoreR.value = lerp(lerp(1.2, 2.4, h), 1.9, blow);
    CU.uHaloI.value = lerp(0.05 + 0.3 * h * h, 0.035, blow);
    CU.uHaloR.value = lerp(lerp(10, 24, h), 26, blow);
    CU.uSpike.value = lerp(smooth(0.85, 1.0, h) * 0.12, 0.16, blow);
    CU.uSpikeL.value = lerp(20, 34, blow);
    if (brand) { CU.uSpike.value *= 0.6; CU.uSpikeL.value *= 0.6; }
    { const coreK = look.coreK || 1; CU.uCoreR.value *= coreK; CU.uHaloR.value *= coreK; }
    // full-res edge pass (filaments, torn wisps, razor rims at silhouettes); k_edge* params for QA
    const phone = opts.mobile || (input.aspect || 1.6) < 1;
    CU.uEdge.value.set(K('k_edge'), 0.5 * K('k_edgeS'), 0.13, (brand ? 0.3 : 0.4 * (r8L ? lerp(1, r8L.rimG, r8 * K('k_rg')) : 1)) * K('k_edgeR'));
    CU.uEdge2.value.set(brand ? 0.6 : 0.3, 0.8, brand ? 1 : 0, phone ? 0.5 : 0.0);
    CU.uEdge3.value.set(5.0 * K('k_edgeW'), 0.42, 1.0, brand ? 0 : 0.55 * blow * K('k_ds'));   // .w r7: near-star dither calm
    // striations off while the visitor holds (the protostar is the subject; keeps the collapse under 8 ms)
    { const st = look.str || [0.8, 1.0, 3.4, 0], sOn = (brand ? 1 : 1 - smooth(0.05, 0.3, h) * pre) * K('k_str');   // off while holding (collapse cost)
      U.uStr.value.set(st[0] * sOn, st[1] * sOn, st[2], st[3]);
      // r7 ignite: the same closed-form glow fills the star's blown cavity (teal core; magenta walls = .w)
      const cg = !brand && look.cavGlow ? look.cavGlow * lit * smooth(0.0, 1.2, tz) * K('k_cg') * (r8L ? 1 + (r8L.push.cg - 1) * push8 * r8 * K('k_pc') : 1) : 0;
      U.uRHz.value.set(look.ring ? (look.rhz || 0.12) * K('k_hz') : cg, look.ring ? look.ring.r * 0.72 : Math.max(U.uGasF.value.z, 0.3) * 0.9 * K('k_cgr'),
        look.eroMix !== undefined ? look.eroMix : 0.5, look.ring ? 0 : (look.cavWall || 0) * K('k_cw') * (r8L ? lerp(1, lerp(r8L.cw, r8L.push.cw, push8 * K('k_pw')), r8 * K('k_cw8')) : 1));
      // phones: the fill reaches the cliff foot (the portrait bottom band was unlit)
      if (look.fill && !brand) U.uFill.value.y = look.fill.y0 - (phone ? 6.0 * K('k_foot') : (r8L ? r8 * r8L.footD * K('k_ft8') : 0.0));   } // r8: desktop fill reaches the lower cliff too
    CU.uEdge4.value.set((brand ? (look.shell || look.ring ? 0 : 0.6) : 0.7 * (1 - smooth(0.05, 0.3, h) * pre)) * K('k_edgeI'), 0.45, brand ? 150 : 160 * blow * K('k_ec'), brand ? 0 : K('k_em'));   // r7: clean cavity (no fibres at the star)
    CU.uEdgeR.value.set(brand ? 20 : lerp(20, 60, h) * pre + 20 * blow, brand ? 70 : lerp(60, 170, h) * pre + 70 * blow);
    CU.uRefR.value = look.ring ? look.ring.r : (look.shell ? look.shell.r : 2.0);
    CU.uRimS.value = brand ? 1 : 0.7 * blow;
    CU.uRimM.value.set(r8L ? r8L.rimM[0] : 0, r8L ? r8L.rimM[1] : 0, r8L ? r8L.rimM[2] : 0, r8 * K('k_rm'));   // r8 magenta rims
    CU.uVisN.value = r8 * K('k_vn');
    CU.uR9C.value.set(r9L ? r9 * r9L.rimB * K('k_rs') * (1 - push8) : 0, r9L ? r9 * r9L.flat * K('k_rp') * (1 - push8) : 0, r9L ? r9L.tapT : 1, r9L ? r9L.rimR : 120);   // r9 crisp rims
    if (r9L) { CU.uR9U.value.fromArray(r9L.umb); CU.uEdge.value.w *= lerp(1, r9L.edgeG, r9 * K('k_eg')); }   // r9: brighter cliff-top rims
    CU.uR9D.value.x = r9L ? r9 * r9L.dome * K('k_dm') * (1 - push8) : 0;   // r9 dome wisps (radius: applyView)
    CU.uHaloC.value.set(r8L ? r8L.haloC[0] : 0, r8L ? r8L.haloC[1] : 0, r8L ? r8L.haloC[2] : 0, r8L ? r8 * push8 * r8L.push.ht * K('k_ht') * (r9L && ph9 ? lerp(1, r9L.htP, r9 * K('k_hp')) : 1) : 0);   // r8: teal push-in halo tail
    CU.uVisMix.value = lerp(h * h * 0.75, 0.0, blow);
    CU.uStarsI.value = lerp(0.85, 1.0, blow);
    let flash = burst * (rebirth ? 0.25 : 0.4);
    let flashR = lerp(30, 160, easeOut(clamp(tz / 0.9, 0, 1)));
    // ignite ending: push into the star, its glow swells for the flash cut
    if (!brand) {
      const fl = smooth(0.8, 1.0, p);
      const flq = r8L && phone ? lerp(fl * fl, fl * fl * fl, r8 * K('k_pq')) : fl * fl;   // r8: phones swell later (the pale wash filled the portrait frame)
      CU.uHaloI.value *= 1 + 30 * flq;
      CU.uHaloR.value *= 1 + 5 * fl;
      flash += 0.5 * Math.pow(fl, 2.2);
      flashR = lerp(flashR, 600, fl);
    }
    CU.uFlash.value = flash;
    CU.uFlashR.value = flashR;
    CU.uFlashCol.value.set(0.82, 0.9, 1.0);
    MOU.uStarL.value = starL * 0.6 + 0.3 * h * h;
    MOU.uCool.value = 0.012 * (1 - 0.5 * blow);
    // grade
    grade.vignette = lerp(0.52, 0.4, blow);
    { const s8 = r8L ? r8L.sh : [0.02, 0.02, 0.09], k8 = r8 * K('k_gs');   // r8: teal (not violet) shadow lift once lit: dark rust stays rust
      grade.shadows[0] = lerp(0.02, s8[0], k8); grade.shadows[1] = lerp(0.02, s8[1], k8); grade.shadows[2] = lerp(0.09, s8[2], k8); }
    grade.bloomK = lerp(0.8, 0.65, blow) + 0.3 * burst;
    grade.exposure = look.expo * lerp(1.0, (input.aspect || 1.6) < 1 ? 0.8 : 0.9, blow) * (1 + 0.8 * burst * K('k_bx')) * (1 + 0.3 * smooth(0.15, 0.6, h) * pre);
    if (r9L && ph9) grade.exposure *= lerp(1, r9L.expoP, r9 * (1 - push8) * K('k_ph'));   // r9: phone lit exposure
    grade.contrast = lerp(1.08, 1.12, blow);
    grade.sat = lerp(1.08, 1.14, blow);
    if (r9L && ph9) grade.sat *= lerp(1, r9L.satP, r9 * push8 * K('k_ph'));   // r9: phone push-in C
    // --- camera
    const lk = input.look || { x: 0, y: 0 };
    S.lookX = +lk.x || 0; S.lookY = +lk.y || 0;
    let d, az, el, ty;
    const C0 = look.cam;
    if (!brand) {
      d = lerp(C0.d, C0.d * 0.62, hE); az = lerp(C0.az, C0.az * 0.4, hE); el = lerp(C0.el, C0.el * 0.5, hE); ty = lerp(C0.ty, 0.0, hE);
      if (lit) {
        const a = easeOut(clamp(tz / 2.6, 0, 1));
        d = lerp(d, C0.d * 0.86, a); ty = lerp(ty, C0.ty * 0.8, a);
        const b = easeInOut(seg(p, 0.12, 0.82));
        d = lerp(d, C0.d * 0.6, b); az = lerp(az, C0.az + 0.22, b); el = lerp(el, C0.el + 0.03, b); ty = lerp(ty, C0.ty * 0.9, b);
        const e = easeInOut(seg(p, 0.82, 1.0));
        d = lerp(d, C0.d * 0.34, e); ty = lerp(ty, 0.0, e);
      }
      const shake = 0.01 * h * h * h * pre + 0.035 * lit * Math.exp(-tz / 0.3);
      az += shake * Math.sin(time * 37.0); el += shake * Math.sin(time * 29.0 + 1.3);
    } else {
      d = lerp(C0.d + 1.2, C0.d, easeInOut(seg(p, 0.05, 1.0))); az = C0.az + 0.14 * (p - 0.6) + 0.02 * Math.sin(time * 0.05);
      el = C0.el; ty = C0.ty;
    }
    const asp = input.aspect || 1.6;
    S.tanV = 0.445 * (asp < 1 ? Math.pow(1 / asp, 0.62) : 1);
    if (asp < 1) d *= 1 + 0.1 * (1 - asp);
    // slow ambient drift + parallax to where the visitor looks
    az += 0.018 * Math.sin(time * 0.043) + S.lookX * 0.07;
    el += 0.012 * Math.sin(time * 0.031 + 1.0) + S.lookY * 0.045;
    orbit(S.camPos, d, az, el);
    S.camTgt.copy(U.uStar.value); S.camTgt.y += ty;
    S.camTgt.x += S.lookX * 0.12; S.camTgt.y += S.lookY * 0.08;
    S.roll = (brand ? C0.roll : C0.roll * (1 - hE)) + 0.02 * Math.sin(time * 0.07);
    S.shiftX = (+P.shiftX || 0);
    S.shiftY = (+P.shiftY || 0) - (asp < 1 ? 0.08 : 0);
    const pt = input.pointer || {};
    const stirOK = (brand ? p > 0.45 : lit && tz > 2.5) && pt.down;
    S.stir = stirOK ? 1 : 0; S.stirX = pt.x == null ? 0.5 : pt.x; S.stirY = pt.y == null ? 0.5 : pt.y;
  }

  // camera + projection dependent uniforms for a given output size
  function applyView(w, h, iw, ih, stirK) {
    const asp = w / h;
    camera.position.copy(S.camPos);
    camera.up.set(Math.sin(S.roll), Math.cos(S.roll), 0);
    camera.lookAt(S.camTgt);
    camera.fov = 2 * Math.atan(S.tanV) * 180 / Math.PI;
    camera.aspect = asp;
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld(true);
    U.uCamMat.value.setFromMatrix4(camera.matrixWorld);
    U.uCamPos.value.copy(camera.position);
    U.uTan.value.set(S.tanV * asp, S.tanV);
    U.uShift.value.set(S.shiftX, -S.shiftY);   // shiftY is CSS-like (+ = down); GL is + up
    U.uPxA.value = 2 * S.tanV / ih;
    CU.uPxA.value = 2 * S.tanV / h;
    CU.uDpr.value = Math.max(1, h / 900);
    CU.uR9D.value.y = U.uRHz.value.y * (look.r9 ? look.r9.domeR : 1.4) / (Math.max(S.camPos.distanceTo(U.uStar.value), 0.1) * CU.uPxA.value * CU.uDpr.value);   // r9: dome radius in px
    CU.uVolTexel.value.set(1 / iw, 1 / ih);
    // infall-streamer frame: 60% of the way from the jet axis toward the camera
    _t.copy(camera.position).sub(U.uStar.value).normalize();
    { const sa = U.uSAx.value.copy(U.uAxis.value).lerp(_t, 0.6).normalize();
      const b1 = U.uSB1.value.copy(U.uAxis.value).cross(sa).normalize(); U.uSB2.value.copy(sa).cross(b1).normalize(); }
    // edge pass: subject plane distance, camera forward, the cluster light direction on screen
    _t.set(0, 0, -1).transformDirection(camera.matrixWorld); CU.uFwd.value.copy(_t);
    CU.uRefD.value = Math.max(0.5, _v.copy(S.camTgt).sub(camera.position).dot(_t));
    _t.copy(U.uLDir.value).transformDirection(camera.matrixWorldInverse);
    { const l = Math.hypot(_t.x, _t.y), k = Math.min(1, l / 0.3), il = l > 1e-4 ? k / l : 0;
      CU.uLDirS.value.set(_t.x * il, _t.y * il + 1 - k).normalize(); }
    MOU.uPx.value = Math.max(1, h / 900);
    _v.copy(U.uStar.value).applyMatrix4(camera.matrixWorldInverse);
    const inFront = _v.z < 0;
    const nx = (_v.x / -_v.z) / (S.tanV * asp) + 2 * S.shiftX, ny = (_v.y / -_v.z) / S.tanV - 2 * S.shiftY;
    const ux = nx * 0.5 + 0.5, uy = ny * 0.5 + 0.5;
    CU.uStarPx.value.set(ux * w, uy * h);
    CU.uStarOn.value = inFront ? 1 : 0;
    anchors.star.x = ux; anchors.star.y = 1 - uy;
    anchors.star.visible = inFront && ux > -0.02 && ux < 1.02 && uy > -0.02 && uy < 1.02;
    if (stirK > 0.001) {
      const sx = (S.stirX * 2 - 1 - 2 * S.shiftX) * S.tanV * asp, sy = (S.stirY * 2 - 1 + 2 * S.shiftY) * S.tanV;
      _t.set(sx, sy, -1).applyMatrix3(U.uCamMat.value).normalize();
      U.uStir.value.set(_t.x, _t.y, _t.z, stirK);
      U.uStirO.value.copy(camera.position);
    } else U.uStir.value.w = 0;
  }

  let stirK = 0;
  const world = {
    scene, camera, grade, anchors,
    stats: { tier: tierName, steps: TIER.steps, internal: [0, 0], macro: TIER.macro, light: TIER.light, detail: N, motes: NM },
    async load() {
      try {
        detail = acquireDetail(renderer, N, (rt) => { bake3D(rt, detMat, N); mip3D(rt); });
        U.uDet.value = detail.rt.texture;
        ensureLook({ mode: 'ignite' });
        // r8: compile the lit programs up front (the switch happens under the ignition burst: no hitch)
        { const pt = renderer.getRenderTarget(); renderer.setRenderTarget(volRTb); marchQ.material = marchMat8; marchQ.render(renderer);
          compQ.material = compMat8; compQ.render(renderer); marchQ.material = marchMat9; marchQ.render(renderer); compQ.material = compMat9; compQ.render(renderer); marchQ.material = marchMat; compQ.material = compMat; renderer.setRenderTarget(pt); }
      } catch (e) {
        console.error('[nebula] bake failed', e);
      }
    },
    resize(w, h) {
      bufW = Math.max(1, w | 0); bufH = Math.max(1, h | 0);
      const pr = renderer.getPixelRatio() || 1;
      // internal res: a per-tier pixel budget (small phone screens get a sharper fraction), clamped
      const cw = bufW / pr, ch = bufH / pr;
      const rs = clamp(Math.sqrt(TIER.px / (cw * ch)), TIER.res, TIER.resMax);
      volW = Math.max(16, Math.round(cw * rs)); volH = Math.max(16, Math.round(ch * rs));
      volRT.setSize(volW, volH); volRT2.setSize(volW, volH);
      volWb = Math.max(16, Math.round(volW * 0.4)); volHb = Math.max(16, Math.round(volH * 0.4));
      volRTb.setSize(volWb, volHb); volRT2b.setSize(volWb, volHb);
      DU.uTexel.value.set(1 / volW, 1 / volH);
      world.stats.internal = [volW, volH];
    },
    update(dt, time, input) {
      const P = input.params || {};
      ensureLook(P);
      U.uTime.value = time;
      computeState(input, time);
      stirK = damp(stirK, S.stir, 4, Math.min(dt || 0.016, 0.1));
      const bd = S.bd = +P.backdrop > 0;
      const iw = bd ? volWb : volW, ih = bd ? volHb : volH;
      // r7: fewer march steps before ignition: -10% at the opening, -30% while the visitor holds (the collapse was the
      // 8.8-9.2 ms beat), ramping back over the first 0.9 s after ignition (under the burst). k_hs / k_h0 = 0 restore
      const ig = S.mode === 'ignite', pre0 = ig ? (S.tau < 0 ? 1 : 1 - smooth(0.0, 0.9, S.tau)) : 0;
      const hs = ig ? smooth(0.05, 0.3, S.hold) * pre0 * kParam(P, 'k_hs') : 0;
      const h0t = ig && S.tau < 0 ? 0.1 * kParam(P, 'k_h0') : 0;
      const st8 = ig && S.r8 && look.r8 ? lerp(1, look.r8.stepL, kParam(P, 'k_st8')) : 1;   // r8: lit step trim (pays for the rim/veil terms; k_st8 = 0 off)
      const st9 = ig && S.r9 && look.r9 ? lerp(1, look.r9.stepL9, kParam(P, 'k_st9')) : 1;   // r9: pre-integrated skins tolerate a coarser step (pays for the r9 terms)
    U.uStepK.value = 1.45 / (bd ? Math.round(TIER.steps * 0.6) : Math.max(Math.round(TIER.steps * st8 * st9 * (1 - h0t - 0.23 * hs)), 16));
      CU.uVolT.value = (bd ? volRT2b : volRT2).texture; DU.uSrc.value = (bd ? volRTb : volRT).texture;
      DU.uTexel.value.set(kParam(P, 'k_den') / iw, kParam(P, 'k_den') / ih);   // exact 3x3 box denoise (k_den = 0: QA probe, no box) (phones add the composite's flat-area smoothing)
      CU.uSoft.value = bd ? 1 : 0;
      if (bd) CU.uStarsI.value = 0;
      if (bd) CU.uEdge.value.x = 0;
      applyView(bufW, bufH, iw, ih, stirK);
    },
    render(target) {
      const sk = world._skip;   // dev profiling: { vis, march, den, comp, motes }
      marchQ.material = S.r9 ? marchMat9 : (S.r8 ? marchMat8 : marchMat); compQ.material = S.r9 ? compMat9 : (S.r8 ? compMat8 : compMat);   // r8/r9 programs only while lit
    if (!sk.vis) { renderer.setRenderTarget(visRT); visQ.render(renderer); }
      const bd = S.bd;
      if (!sk.march) { renderer.setRenderTarget(bd ? volRTb : volRT); marchQ.render(renderer); }
      if (!sk.den) { renderer.setRenderTarget(bd ? volRT2b : volRT2); denQ.render(renderer); }
      renderer.setRenderTarget(target);
      if (!sk.comp) compQ.render(renderer);
      if (!sk.motes && !bd) {
        const ac = renderer.autoClear;
        renderer.autoClear = false;
        renderer.render(scene, camera);
        renderer.autoClear = ac;
      }
    },
    _skip: {},
    snapshot(w = 2160, h = 2700) { return snapshot(w, h); },
    _dbg: { volRT, visRT, U, CU, DU, S, MU, compMat, marchMat, compMat8, marchMat8, compMat9, marchMat9 },
    dispose() {
      [volRT, volRT2, volRTb, volRT2b, visRT, macroRT, litRT].forEach((t) => t.dispose());
      if (detail) { releaseDetail(renderer, N); detail = null; }
      [marchMat, marchMat8, marchMat9, visMat, compMat, compMat8, compMat9, denMat, detMat, macroMat, litMat, moteMat].forEach((m) => m.dispose());
      if (snapMat) snapMat.dispose(); if (snapMat8) snapMat8.dispose();
      [marchQ, visQ, compQ, denQ, bakeQ].forEach((q) => q.dispose());
      mgeo.dispose();
      if (post) post.dispose();
    },
  };
  /* ---------------- snapshot (poster) ---------------- */
  // Re-renders the current state at w x h with more march steps and a higher internal
  // resolution, runs a copy of the site's post (bloom -> ACES -> grade -> vignette ->
  // dither, no grain) and reads the pixels into a 2D canvas.
  let snapMat = null, snapMat8 = null, snapMat9 = null, post = null;
  function makePost() {
    const q = (frag, u) => new FullScreenQuad(new THREE.ShaderMaterial({ vertexShader: G.VERT, fragmentShader: frag, uniforms: u, depthTest: false, depthWrite: false }));
    const down = q(G.POST_DOWN, { src: F(null), texel: V2(), first: F(0), threshold: F(0.9) });
    const up = q(G.POST_UP, { src: F(null), base: F(null), texel: V2() });
    const fin = q(G.POST_FINAL, {
      scene: F(null), bloom: F(null), bloomK: F(0.8), exposure: F(1), vignette: F(0.35), sat: F(1.05), contrast: F(1.04),
      shadows: V3(), highs: V3(), gainC: V3(), res: V2(),
    });
    return {
      down, up, fin,
      dispose() { [down, up, fin].forEach((x) => { x.material.dispose(); x.dispose(); }); },
    };
  }
  function snapshot(w, h) {
    const maxT = Math.min(gl.getParameter(gl.MAX_TEXTURE_SIZE), gl.getParameter(gl.MAX_RENDERBUFFER_SIZE), 8192);
    const k = Math.min(1, maxT / Math.max(w, h));
    w = Math.max(16, Math.round(w * k)); h = Math.max(16, Math.round(h * k));
    const r8s = !!S.r8, r9s = !!S.r9; let smat = r9s ? snapMat9 : (r8s ? snapMat8 : snapMat);
    if (!smat) { smat = mkMat(MARCH, U, Object.assign(marchDefs(TIER.snapSteps, 1, 1, 1), { R8: r8s ? 1 : 0, R9: r9s ? 1 : 0 })); if (r9s) snapMat9 = smat; else if (r8s) snapMat8 = smat; else snapMat = smat; }
    compQ.material = r9s ? compMat9 : (r8s ? compMat8 : compMat);
    if (!post) post = makePost();
    const iw = Math.round(w * 0.7), ih = Math.round(h * 0.7);
    const vrt = mkRT(iw, ih), vrt2 = mkRT(iw, ih), hdr = mkRT(w, h), ldr = mkRT(w, h, THREE.UnsignedByteType);
    const levels = 6, dn = [], upT = [];
    let lw = w, lh = h;
    for (let i = 0; i < levels; i++) { lw = Math.max(1, lw >> 1); lh = Math.max(1, lh >> 1); dn.push(mkRT(lw, lh)); upT.push(mkRT(lw, lh)); }
    const ac = renderer.autoClear;
    const prevStep = U.uStepK.value, prevSoft = CU.uSoft.value, prevSI = CU.uStarsI.value; CU.uSoft.value = 0; if (S.bd) { CU.uStarsI.value = 1; CU.uEdge.value.x = 1; }
    try {
      U.uStepK.value = 1.45 / TIER.snapSteps;
      applyView(w, h, iw, ih, 0);
      renderer.setRenderTarget(visRT); visQ.render(renderer);
      const mq = new FullScreenQuad(smat);
      renderer.setRenderTarget(vrt); mq.render(renderer);
      mq.dispose();
      DU.uSrc.value = vrt.texture; DU.uTexel.value.set(1 / iw, 1 / ih);
      renderer.setRenderTarget(vrt2); denQ.render(renderer);
      CU.uVolT.value = vrt2.texture;
      renderer.setRenderTarget(hdr); compQ.render(renderer);
      renderer.autoClear = false;
      renderer.render(scene, camera);
      renderer.autoClear = ac;
      // bloom
      const du = post.down.material.uniforms, uu = post.up.material.uniforms, fu = post.fin.material.uniforms;
      du.threshold.value = grade.threshold != null ? grade.threshold : 0.9;
      let src = hdr;
      for (let i = 0; i < levels; i++) {
        du.src.value = src.texture; du.texel.value.set(1 / src.width, 1 / src.height); du.first.value = i === 0 ? 1 : 0;
        renderer.setRenderTarget(dn[i]); post.down.render(renderer); src = dn[i];
      }
      let small = dn[levels - 1];
      for (let i = levels - 2; i >= 0; i--) {
        uu.src.value = small.texture; uu.base.value = dn[i].texture; uu.texel.value.set(0.5 / small.width, 0.5 / small.height);
        renderer.setRenderTarget(upT[i]); post.up.render(renderer); small = upT[i];
      }
      fu.scene.value = hdr.texture; fu.bloom.value = small.texture;
      const g = grade;
      fu.bloomK.value = g.bloomK; fu.exposure.value = g.exposure; fu.vignette.value = g.vignette * 0.8;
      fu.sat.value = g.sat; fu.contrast.value = g.contrast;
      fu.shadows.value.fromArray(g.shadows); fu.highs.value.fromArray(g.highs); fu.gainC.value.fromArray(g.gainC);
      fu.res.value.set(w, h);
      renderer.setRenderTarget(ldr); post.fin.render(renderer);
      const buf = new Uint8Array(w * h * 4);
      renderer.readRenderTargetPixels(ldr, 0, 0, w, h, buf);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      const img = ctx.createImageData(w, h);
      const row = w * 4;
      for (let y = 0; y < h; y++) img.data.set(buf.subarray((h - 1 - y) * row, (h - y) * row), y * row);
      ctx.putImageData(img, 0, 0);
      return canvas;
    } finally {
      renderer.autoClear = ac;
      U.uStepK.value = prevStep; CU.uSoft.value = prevSoft; CU.uStarsI.value = prevSI;
      CU.uVolT.value = volRT2.texture; DU.uSrc.value = volRT.texture; DU.uTexel.value.set(1 / volW, 1 / volH);
      renderer.setRenderTarget(null);
      [vrt, vrt2, hdr, ldr, ...dn, ...upT].forEach((t) => t.dispose());
      applyView(bufW, bufH, volW, volH, stirK);
    }
  }

  return world;
}
